// 1. Keep the type-only import for the compiler
import type { LuaEngine } from "wasmoon";

// 2. Destructure only the values that exist at runtime from the window
const { LuaFactory, LuaLibraries } = window.wasmoon;
import { MissionConfig } from "./types/missionConfig";
import { SimulationState } from "./types/simulationState";
import { EventBus } from "./EventBus";
import { registerLuaDefinition } from "./CodeEditor";

const LUA_FACTORY = new LuaFactory();
const SUPPORTED_LUA_LIBS = [
    LuaLibraries.Base,
    LuaLibraries.Math,
    LuaLibraries.String,
    LuaLibraries.Table,
];
const LUA_SANDBOX_CONFIG = {
    // Don't inject lua libs we will load them manually
    openStandardLibs: false,
    // Injects some JS objects to the Lua environment: Error, Promise, null, Objects
    injectObjects: true,
    // Enables the proxy for JS objects, useful for classes, etc...
    enableProxy: false,
    // Whether to trace memory allocations.
    traceAllocations: false,
    // Maximum time in milliseconds a Lua function can run before being interrupted.
    functionTimeout: 1000
};

export class SimulationEvents {
    static readonly SET_LIFT_TARGET = 'SET_LIFT_TARGET';
}

export interface SetLiftTargetEvent {
    liftId: string;
    targetFloors: number[];
}

// This provides autocomplete and type checking in the editor
registerLuaDefinition(`
--- Set the targets of a lift
--- @param liftId string
--- @param floors number[]
--- @return boolean success
function setLiftTargets(liftId, floors) end
`);

export class CodeSandbox {
    private readonly engine: LuaEngine;

    constructor(engine: LuaEngine, code: string) {
        this.engine = engine
        this.engine.doString(code);
    }

    close(): void {
        this.engine.global.close();
    }

    async onInit(missionConfig: MissionConfig): Promise<void> {
        return this._callMethod('onInit', [missionConfig]);
    }

    async onTick(simulation: SimulationState): Promise<void> {
        return this._callMethod('onTick', [simulation.generation, simulation.building]);
    }

    async onLiftMoveStart(liftId: string, startingFloor: number, simulation: SimulationState): Promise<void> {
        return this._callMethod('onLiftMoveStart', [liftId, startingFloor, simulation.building]);
    }

    async onLiftMoveEnd(liftId: string, endingFloor: number, simulation: SimulationState): Promise<void> {
        return this._callMethod('onLiftMoveEnd', [liftId, endingFloor, simulation.building]);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async _callMethod(methodName: string, args: any[]): Promise<void> {
        if (typeof this.engine.global.get(methodName) !== 'function') {
            return Promise.resolve();
        }
        try {
            await this.engine.global.call(methodName, ...args);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Lua execution error:", err);
        }
    }

    // DEBUG only
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    debug_executeLuaScript(script: string): Promise<any> {
        return this.engine.doString(script);
    }

    private static registerBindings(engine: LuaEngine, logFn: (message: string, ...args: unknown[]) => void): void {
        const api = {
            setLiftTargets: (liftId: string, floors: number[]): boolean => {
                EventBus.get().emit(SimulationEvents.SET_LIFT_TARGET, {
                    liftId,
                    targetFloors: floors
                } as SetLiftTargetEvent);
                return true;
            }
        };

        // Register API functions
        for (const [name, fn] of Object.entries(api)) {
            engine.global.set(name, fn);
        }

        // Route print commands to a custom handler
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        engine.global.set('print', (...args: any[]) => {
            const [logStatement, ...rest] = args;
            logFn(logStatement.toString(), ...rest);
        });
    }

    static async open(code: string, logFn: (message: string, ...args: unknown[]) => void): Promise<CodeSandbox> {
        const engine = await LUA_FACTORY.createEngine(LUA_SANDBOX_CONFIG);
        for (const lib of SUPPORTED_LUA_LIBS) {
            await engine.global.loadLibrary(lib);
        }
        CodeSandbox.registerBindings(engine, logFn);
        return new CodeSandbox(engine, code);
    }
}



export const LUA_TEMPLATES = {
    MAIN: `

local hasInitializedLifts = false;

--- Called before the simulation starts
--- Can be used to initialize some custom state before the first cycle starts.
--- @param config MissionConfig
function onInit(config) 
    print("onInit")
end

--- Called every tick (typically every second) with the current game state
--- Can be used to process the requests: setting lift targets, picking up passengers, etc.
--- @param generation number
--- @param building BuildingState
function onTick(generation, building)
    print("onTick", generation)
    -- Sample code sending the lifts to a random set of floors
    if not hasInitializedLifts then
        hasInitializedLifts = true
        for liftId, liftState in pairs(building.lifts) do
            if #liftState.targetedFloors == 0 then
                setLiftTargets(liftId, {2, 4, 1, 3, 2})
            end        
        end    
    end

    -- Sample code to check active requests
    -- within a lift requests are for a specific floor number
    for liftId, liftState in pairs(building.lifts) do
        -- check lift direction based on targetedFloors
        -- Reminder: LUA is 1-based index
        local liftDirection = nil
        if #liftState.targetedFloors == 0 then
            liftDirection = nil -- not moving        
        elseif liftState.currentFloor < liftState.targetedFloors[1] then
            liftDirection = 'up'
        elseif liftState.currentFloor > liftState.targetedFloors[1] then
            liftDirection = 'down'
        end
        print('Lift Direction ',liftId, liftDirection)
        print('Lift Status', liftId, liftState.status)

        for _, request in pairs(liftState.requests) do
            -- can use requestId to dedupe requests you have already accounted for
            local requestId = request.requestId
            if request.requestType == 'floor' then
                -- one or more visitors are requesting this floor from within the lift
                local requestedFloor = request.requestData.requestedFloor
                -- TODO
            end
        end
    end
    -- at floor level, requests depend on the floor buttonType
    for floorNum, floorState in pairs(building.floors) do
        for _, request in pairs(floorState.requests) do
            -- can use requestId to dedupe requests you have already accounted for
            local requestId = request.requestId
            if request.requestType == 'floor' then
                 -- visitors are requesting to go to a specific floor
                local requestedFloor = request.requestData.requestedFloor
                -- TODO
            elseif request.requestType == 'direction' then
                -- visitors want to go UP or DOWN
                local requestedDirection = request.requestData.requestedDirection
                if requestedDirection == LiftCallDirection.UP then
                    -- TODO
                elseif requestedDirection == LiftCallDirection.DOWN then
                    -- TODO
                end                
            elseif request.requestType == 'single' then
                -- visitors are requesting a lift at this floor                
                -- TODO
                print('Single Request', floorNum)
            end
        end
    end
end

--- Called when a lift starts moving
--- @param liftId string
--- @param startingFloor number
--- @param building BuildingState
function onLiftMoveStart(liftId, startingFloor, building)
    -- print("onLiftMoveStart", liftId, startingFloor)
end

--- Called when a lift stops moving
--- @param liftId string
--- @param endingFloor number
--- @param building BuildingState
function onLiftMoveEnd(liftId, endingFloor, building)
    -- print("onLiftMoveEnd", liftId, endingFloor)
end
`
}

export const LUA_CODE_SNIPPETS = [
    {
        title: 'Basic Movement',
        description: 'Send a lift to a specific floor.',
        code: `
setLiftTargets("lift0", {1})
`
    },
    {
        title: 'Sequential Targets',
        description: 'Send a lift to multiple floors in order.',
        code: `
setLiftTargets("lift0", {2, 4, 1, 3})
`
    },
    {
        title: 'Iterate Lifts',
        description: 'Loop through all lifts in the building.',
        code: `
for liftId, liftState in pairs(building.lifts) do
    -- your logic here
end
`
    },
    {
        title: 'Check Weight',
        description: 'Check if a lift has reached its weight limit.',
        code: `
if liftState.currentWeight >= liftState.config.weightLimit then
    -- lift is full
end
`
    },
    {
        title: 'Floor Requests',
        description: 'Iterate through waiting visitors on a floor.',
        code: `
for floorNum, floorState in pairs(building.floors) do
    if #floorState.visitors > 0 then
        -- floor has waiting visitors
    end
end
`
    }
];
